import React, { useState } from "react"
import dayjs from "utils/dates"
import {
  Form,
  Input,
  DatePicker,
  TimePicker,
  InputNumber,
  Button,
  message,
  Typography,
  Divider,
  Avatar,
  Space,
} from "antd"

// firebase
import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore"
import { Firestore } from "services/firebase"

// componets
import AuthBox from "components/AuthBox"
import ReviewBox from "./ReviewBox"
import UserInfo from "./UserInfo"

// store
import { useTranslation } from "react-i18next"
import { useAuth } from "store/auth"

import { useReservations } from "store/reservations"

// config
import config from "config"

const TabReservations = () => {
  const { isAuthentificated, user } = useAuth()

  const reservations = useReservations()

  const [messageApi, contextHolder] = message.useMessage()

  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()

  const [state, setState] = React.useState("idle")

  const [currentReservation, setCurrentReservation] = React.useState()

  const handleSubmit = async (values) => {
    setLoading(true)
    try {
      // Save reservation to Firestore
      const reservation = {
        firstname: values.firstname,
        lastname: values.lastname,
        phone: values.phone,
        datetime: new Date(
          `${values.date.format("YYYY-MM-DD")} ${values.time.format("HH:mm")}`
        ),
        people: values.people,
        note: values.note || null,
        resto_key: config.key,
        created_at: serverTimestamp(),
        email: user.email || null,
        status: "created",
        creator_uid: user.uid,
        userId: user.uid || "anonymous",
      }
      console.log("Reservation data:", reservation)
      const res = await addDoc(
        collection(Firestore, "reservations"),
        reservation
      )

      setCurrentReservation({
        ...reservation,
        id: res.id,
      })

      messageApi.success(t("reservation_success"))
      form.resetFields()
    } catch (error) {
      console.error("Error saving reservation:", error)
      messageApi.error(t("reservation_error"))
    } finally {
      setLoading(false)
    }
  }

  if (isAuthentificated && currentReservation) {
    const userReservation = reservations.list.find(
      (reservation) => reservation.id === currentReservation.id
    )

    return (
      <div>
        {contextHolder}

        {(() => {
          switch (userReservation?.status) {
            case "created":
              return (
                <>
                  <Avatar
                    src="icon-process/waiting.gif"
                    size={40}
                    alt="icon-process-waiting"
                  />

                  <Typography.Paragraph>
                    {t("wait_moment")}
                  </Typography.Paragraph>

                  <Typography.Paragraph>
                    {t("reservation_received")}
                  </Typography.Paragraph>

                  <Space>
                    <Button
                      type="primary"
                      onClick={async () => {
                        setCurrentReservation(null)
                        setState("idle")
                      }}
                    >
                      {t("done")}
                    </Button>

                    <Button
                      danger
                      loading={
                        state === "storno-reservation" ? true : undefined
                      }
                      type="primary"
                      onClick={async () => {
                        try {
                          setState("storno-reservation")

                          await updateDoc(
                            doc(Firestore, "reservations", userReservation.id),
                            { status: "cancelled-by-user" }
                          )

                          setState("idle")
                        } catch (e) {
                          messageApi.error(e.message)
                          console.error(e)
                        }
                      }}
                    >
                      {t("cancel_reservation")}
                    </Button>
                  </Space>
                </>
              )

            case "accepted":
              return (
                <>
                  <Avatar
                    src="icon-process/working.gif"
                    size={40}
                    alt="icon-process-working"
                  />

                  <Typography.Paragraph>
                    {t("thank_you_caps")}
                  </Typography.Paragraph>
                  <Typography.Paragraph>
                    {t("we_received_reservation")}
                  </Typography.Paragraph>
                  <Typography.Paragraph>
                    {t("reservation_accepted")}
                  </Typography.Paragraph>

                  <Button
                    type="primary"
                    onClick={async () => {
                      setCurrentReservation(null)
                      setState("idle")
                    }}
                  >
                    {t("done")}
                  </Button>

                  <Divider />

                  <ReviewBox />
                </>
              )

            case "refused":
              return (
                <>
                  <Avatar
                    src="icon-process/refuse.png"
                    size={40}
                    alt="icon-process-refuse"
                  />

                  <Typography.Paragraph>
                    {t("reservation_refused")}
                    <br />
                    {t("please_contact_phone")}
                  </Typography.Paragraph>
                  <Typography.Paragraph>
                    {config.phones.join(", ")}
                  </Typography.Paragraph>
                  <Typography.Paragraph>{t("thank_you")}</Typography.Paragraph>

                  <Button
                    type="primary"
                    onClick={async () => {
                      setCurrentReservation(null)
                      setState("idle")
                    }}
                  >
                    {t("done")}
                  </Button>
                </>
              )

            case "cancelled-by-user":
              return (
                <>
                  <Avatar
                    src="icon-process/refuse.png"
                    size={40}
                    alt="icon-process-refuse"
                  />

                  <Typography.Paragraph>
                    {t("reservation_cancelled")}
                  </Typography.Paragraph>

                  <Button
                    type="primary"
                    onClick={async () => {
                      setCurrentReservation(null)
                      setState("idle")
                    }}
                  >
                    {t("done")}
                  </Button>
                </>
              )

            default:
              return "undefined-status"
          }
        })()}
      </div>
    )
  }

  return (
    <div>
      {contextHolder}

      <Typography.Title
        level={3}
        style={{
          // color: config.textColor,
          fontStyle: "italic",
        }}
      >
        {t("make_reservation")}
      </Typography.Title>

      {isAuthentificated && <UserInfo />}

      {isAuthentificated && <Divider />}

      {!isAuthentificated ? (
        <AuthBox />
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            firstname: user.data?.firstname,
            lastname: user.data?.familyname,
            phone: user.data?.tel,
          }}
          style={{ maxWidth: 600 }}
        >
          <Form.Item
            label={t("first_name")}
            name="firstname"
            rules={[
              {
                required: true,
                message: t("enter_first_name"),
              },
            ]}
          >
            <Input placeholder={t("enter_first_name")} />
          </Form.Item>

          <Form.Item
            label={t("last_name")}
            name="lastname"
            rules={[
              {
                required: true,
                message: t("enter_last_name"),
              },
            ]}
          >
            <Input placeholder={t("enter_last_name")} />
          </Form.Item>

          <Form.Item
            label={t("phone_number")}
            name="phone"
            rules={[
              {
                required: true,
                message: t("enter_phone"),
              },
            ]}
          >
            <Input placeholder={t("enter_phone")} />
          </Form.Item>

          <Form.Item
            label={t("date")}
            name="date"
            rules={[
              {
                required: true,
                message: t("please_select_date"),
              },
            ]}
          >
            <DatePicker
              style={{ width: "100%" }}
              placeholder={t("select_date")}
              format={t("date_format")}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                onClick={() => {
                  form.setFieldsValue({ date: dayjs() })
                }}
              >
                Heute
              </Button>
              <Button
                onClick={() => {
                  form.setFieldsValue({ date: dayjs().add(1, "day") })
                }}
              >
                Morgen
              </Button>
              <Button
                onClick={() => {
                  form.setFieldsValue({ date: dayjs().add(2, "day") })
                }}
              >
                Übermorgen
              </Button>
            </Space>
          </Form.Item>

          <Form.Item
            label={t("time")}
            name="time"
            rules={[
              {
                required: true,
                message: t("please_select_time"),
              },
            ]}
          >
            <TimePicker
              style={{ width: "100%" }}
              placeholder={t("select_time")}
              format="HH:mm"
              minuteStep={10}
              hideDisabledOptions
              disabledTime={() => ({
                disabledHours: () => [...Array(8).keys()], // Remove hours before 8
              })}
            />
          </Form.Item>

          <Form.Item>
            <Space wrap>
              {[
                "17:00",
                "17:30",
                "18:00",
                "18:30",
                "19:00",
                "19:30",
                "20:00",
                "20:30",
                "21:00",
              ].map((time) => (
                <Button
                  key={time}
                  onClick={() => {
                    form.setFieldsValue({ time: dayjs(time, "HH:mm") })
                  }}
                >
                  {time}
                </Button>
              ))}
            </Space>
          </Form.Item>

          <Form.Item
            label={t("number_of_people")}
            name="people"
            rules={[
              {
                required: true,
                message: t("please_enter_people"),
              },
            ]}
          >
            <InputNumber
              min={1}
              max={20}
              style={{ width: "100%" }}
              placeholder="0"
            />
          </Form.Item>

          <Form.Item label={t("note_for_reservation")} name="note">
            <Input.TextArea
              rows={5}
              placeholder={t("note_reservation_placeholder")}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t("submit_reservation")}
            </Button>
          </Form.Item>
        </Form>
      )}
    </div>
  )
}

export default TabReservations
